import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout, PMDonutChartCard } from "@pimo/pimo-components";

import { TestAppState } from "../../../app";
import { getDropdownCount } from "../../../utils/dropdown-options";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import { getProjectName } from "../../../utils/get-plural-of-project-name";
import { isFieldEnabled } from "../../field-enabled";
import { makeGetCardCopy } from "../card-copy";

/**
 * Adds the status, semaphore-status and completion donut charts, in that
 * order (#2222).
 */
export function addDonutChartCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const language = app.getAppState().language || "en";
  const getCardCopy = makeGetCardCopy(app);

  const statusDonutComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "statusDonutComponent",
      labels: ["donutChart", "chart", "status"],
      description: getCardCopy(language, "reportItemStatusDistribution")
        .description,
      title: getCardCopy(language, "reportItemStatusDistribution").title,
    },
  });
  statusDonutComponent.mapState(({ currentReport, settings, language }) => {
    const statusCounts =
      currentReport?.project?._calculated?.statusCounts ?? {};

    const data =
      settings?.configuration?.statusDefinition?.map((entry) => {
        return {
          label: entry.name,
          value: getDropdownCount(statusCounts, entry),
          color: entry.color ?? "",
        };
      }) ?? [];

    return {
      title: getLocalizedCardCopy(language, "reportItemStatusDistribution", {
        itemName: getItemName(settings?.configuration?.itemName, language),
        itemNamePlural: getPluralOfItemName(
          settings?.configuration?.itemName,
          language
        ),
        projectName: getProjectName(
          settings?.configuration?.projectName,
          language
        ),
      }).title,
      showInfoIcon: false,
      showMenuIcon: false,
      data,
    };
  });

  statusDonutComponent.mapVisibility(({ settings }) => {
    return (
      isFieldEnabled(settings, "status", "itemReporting") &&
      (settings?.configuration?.statusReportingLevel ?? "overall") === "overall"
    );
  });

  const semaphoreStatusOfItemsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "statusOfItemsDonutChartComponent",
      title: "",
      description: "",
      labels: ["donutChart", "chart", "status"],
    },
  });
  semaphoreStatusOfItemsDonutChartComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "statusOfItemsDonutChartComponent",
      labels: ["donutChart", "chart", "semaphoreStatus"],
      title: getCardCopy(language, "reportItemStatusDistribution").title,
      description: getCardCopy(language, "reportItemStatusDistribution")
        .description,
    };
  });

  semaphoreStatusOfItemsDonutChartComponent.mapState(
    ({ currentReport, settings, language }) => {
      const statusCounts =
        currentReport?.project?._calculated?.semaphoreStatusCounts ?? {};
      const data =
        [
          settings?.configuration?.semaphoreStatusDefinition?.[0].low,
          settings?.configuration?.semaphoreStatusDefinition?.[0].medium,
          settings?.configuration?.semaphoreStatusDefinition?.[0].high,
        ]?.map((entry) => {
          return {
            label: entry?.name ?? "",
            value: (entry && getDropdownCount(statusCounts, entry)) ?? 0,
            color: entry?.color ?? "",
          };
        }) ?? [];
      return {
        title: getLocalizedCardCopy(language, "reportItemStatusDistribution", {
          itemName: getItemName(settings?.configuration?.itemName, language),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            language
          ),
          projectName: getProjectName(
            settings?.configuration?.projectName,
            language
          ),
        }).title,
        icon: "DonutLarge",
        data,
      };
    }
  );

  semaphoreStatusOfItemsDonutChartComponent.mapVisibility(({ settings }) => {
    return (
      isFieldEnabled(settings, "status", "itemReporting") &&
      (settings?.configuration?.statusReportingLevel ?? "overall") ===
        "category"
    );
  });

  const completionDonutComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "completionDonutComponent",
      labels: ["donutChart", "chart", "completion"],
      description: getCardCopy(language, "reportItemCompletionDistribution")
        .description,
      title: getCardCopy(language, "reportItemCompletionDistribution").title,
    },
  });
  completionDonutComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "completionDonutComponent",
      labels: ["donutChart", "chart", "completion"],
      title: getCardCopy(language, "reportItemCompletionDistribution").title,
      description: getCardCopy(language, "reportItemCompletionDistribution")
        .description,
    };
  });
  completionDonutComponent.mapState(({ currentReport, settings }) => {
    const completionCounts =
      currentReport?.project?._calculated?.degreeOfCompletionCounts ?? {};
    const data =
      settings?.configuration?.degreeOfCompletionDefinition?.map((entry) => {
        return {
          label: entry.name ?? "",
          value: getDropdownCount(completionCounts, entry),
          color: entry.color ?? "",
        };
      }) ?? [];

    return {
      title: getLocalizedCardCopy(
        language,
        "reportItemCompletionDistribution",
        {
          itemName: getItemName(settings?.configuration?.itemName, language),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            language
          ),
          projectName: getProjectName(
            settings?.configuration?.projectName,
            language
          ),
        }
      ).title,
      showInfoIcon: false,
      showMenuIcon: false,
      data,
    };
  });

  completionDonutComponent.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "completion", "itemReporting")
  );
}
